/**
 * Map pointer + keyboard authoring for the studio.
 *
 * Modelled on `demo/masters-planner/hooks/useTaxiEditorInteraction.ts` rather
 * than the ramp's `useAircraftDragInteraction`: hit-testing in SCREEN space (by
 * projecting each actor through the same path the overlays use) is far simpler
 * than 3D raycasting, needs no scene-graph access, and is accurate enough for
 * composing a shot. It also keeps the studio free of `aircraftEditorStore`,
 * which is wired to real allocations.
 *
 * Modes:
 *  - select : click to select, drag to move, Shift+drag to rotate
 *  - place  : click drops the armed aircraft type
 *  - path   : click appends a path waypoint to the selected actor; drag moves one
 *
 * Keyboard: Escape (back to select / deselect), Delete (remove selection),
 * Ctrl+D (duplicate), R / Shift+R (rotate 5 deg).
 *
 * The scene origin is fixed at map load and must never be `map.getCenter()`,
 * which moves with the camera.
 */
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { screenToWorldPosition } from '@/utils/coordinates'
import { METERS_TO_FEET } from '@/constants/units'
import type { StudioCatalog } from '../data/loadStudioCatalog'
import { useStudioStore, selectActiveScene } from '../store/studioStore'

/** Screen-space grab radius for a path waypoint handle. */
const WAYPOINT_HIT_PX = 12

/** Minimum pointer travel before a press becomes a drag. */
const DRAG_THRESHOLD_PX = 4

/** Floor on an actor's screen hit radius so small types stay grabbable. */
const MIN_ACTOR_HIT_FT = 25

/** Degrees per rotate keystroke. */
const ROTATE_STEP_DEG = 5

interface Params {
  map: mapboxgl.Map | null
  /** Scene origin [lng, lat], fixed at map load. */
  center: [number, number] | null
  catalog: StudioCatalog | undefined
  enabled: boolean
}

export function useStudioInteraction({ map, center, catalog, enabled }: Params) {
  const dragActorId = useRef<string | null>(null)
  const dragWaypointId = useRef<string | null>(null)
  const dragStartScreen = useRef<[number, number]>([0, 0])
  const dragStartWorld = useRef<[number, number]>([0, 0])
  const dragStartPos = useRef<[number, number]>([0, 0])
  const dragStartHeading = useRef(0)
  const rotating = useRef(false)
  const moved = useRef(false)

  useEffect(() => {
    if (!map || !center || !enabled) return
    const canvas = map.getCanvas()

    /** Scene feet -> screen px, matching what the overlays draw. */
    const toScreen = (pos: [number, number]): [number, number] => {
      const ll = CoordinateService.scenePositionToLngLat([pos[0], 0, pos[1]], center)
      const p = map.project(ll as [number, number])
      return [p.x, p.y]
    }

    /** Pointer -> scene feet, via the proven Mapbox unproject path. */
    const toWorldFeet = (clientX: number, clientY: number): [number, number] | null => {
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      )
      const world = screenToWorldPosition(ndc, map, center)
      if (!world) return null
      return [world.x * METERS_TO_FEET, world.z * METERS_TO_FEET]
    }

    const pointerScreen = (e: MouseEvent): [number, number] => {
      const rect = canvas.getBoundingClientRect()
      return [e.clientX - rect.left, e.clientY - rect.top]
    }

    /** Nearest actor under the pointer, using its footprint as the hit radius. */
    const pickActor = (screen: [number, number]): string | null => {
      const state = useStudioStore.getState()
      const scene = selectActiveScene(state)
      if (!scene) return null

      let best: string | null = null
      let bestDist = Infinity
      for (const actor of scene.aircraft) {
        const [sx, sy] = toScreen(actor.position)
        const dist = Math.hypot(screen[0] - sx, screen[1] - sy)

        // Convert the actor's footprint to a screen radius by projecting a point
        // that far away — keeps the hit area honest at any zoom.
        const entry = catalog?.get(actor.typeCode)
        const spanFt = Math.max(
          MIN_ACTOR_HIT_FT,
          entry ? Math.max(entry.dimensions.wingspan, entry.dimensions.length) / 2 : MIN_ACTOR_HIT_FT
        )
        const [ex, ey] = toScreen([actor.position[0] + spanFt, actor.position[1]])
        const radiusPx = Math.max(10, Math.hypot(ex - sx, ey - sy))

        if (dist <= radiusPx && dist < bestDist) {
          bestDist = dist
          best = actor.id
        }
      }
      return best
    }

    /** Path waypoint handle under the pointer, for the selected actor only. */
    const pickWaypoint = (screen: [number, number]): string | null => {
      const state = useStudioStore.getState()
      const scene = selectActiveScene(state)
      const actor = scene?.aircraft.find((a) => a.id === state.selectedActorId)
      if (!actor?.path) return null
      for (const wp of actor.path.waypoints) {
        const [sx, sy] = toScreen(wp.pos)
        if (Math.hypot(screen[0] - sx, screen[1] - sy) <= WAYPOINT_HIT_PX) return wp.id
      }
      return null
    }

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return
      const state = useStudioStore.getState()
      const screen = pointerScreen(e)
      moved.current = false
      dragStartScreen.current = screen

      // Place mode: a click drops the armed type. Handled on mouseup so a
      // click-drag to pan the map doesn't place by accident.
      if (state.tool === 'place') return

      const world = toWorldFeet(e.clientX, e.clientY)
      if (!world) return
      dragStartWorld.current = world

      if (state.tool === 'path') {
        const wpId = pickWaypoint(screen)
        if (wpId) {
          const scene = selectActiveScene(state)
          const actor = scene?.aircraft.find((a) => a.id === state.selectedActorId)
          const wp = actor?.path?.waypoints.find((w) => w.id === wpId)
          if (wp) {
            dragWaypointId.current = wpId
            dragStartPos.current = [wp.pos[0], wp.pos[1]]
            state.selectWaypoint(wpId)
            map.dragPan.disable()
            e.preventDefault()
          }
        }
        return
      }

      // Select mode
      const actorId = pickActor(screen)
      if (!actorId) return
      const scene = selectActiveScene(state)
      const actor = scene?.aircraft.find((a) => a.id === actorId)
      if (!actor) return

      state.selectActor(actorId)
      dragActorId.current = actorId
      dragStartPos.current = [actor.position[0], actor.position[1]]
      dragStartHeading.current = actor.headingDeg
      rotating.current = e.shiftKey
      map.dragPan.disable()
      e.preventDefault()
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!dragActorId.current && !dragWaypointId.current) return
      const screen = pointerScreen(e)
      if (
        !moved.current &&
        Math.hypot(screen[0] - dragStartScreen.current[0], screen[1] - dragStartScreen.current[1]) <
          DRAG_THRESHOLD_PX
      ) {
        return
      }
      moved.current = true

      const state = useStudioStore.getState()

      if (dragActorId.current && rotating.current) {
        // Shift+drag rotates: the heading follows the pointer's bearing from the
        // actor's own position. Scene +Z is south, so compass = atan2(dx, -dz).
        const world = toWorldFeet(e.clientX, e.clientY)
        if (!world) return
        const dx = world[0] - dragStartPos.current[0]
        const dz = world[1] - dragStartPos.current[1]
        if (Math.hypot(dx, dz) < 5) return
        const compass = (Math.atan2(dx, -dz) * 180) / Math.PI
        state.updateAircraft(dragActorId.current, { headingDeg: ((compass % 360) + 360) % 360 })
        return
      }

      const world = toWorldFeet(e.clientX, e.clientY)
      if (!world) return
      // Total-delta drag: offset from the press point, so the object doesn't
      // creep relative to the cursor over a long drag.
      const nx = dragStartPos.current[0] + (world[0] - dragStartWorld.current[0])
      const nz = dragStartPos.current[1] + (world[1] - dragStartWorld.current[1])

      if (dragWaypointId.current && state.selectedActorId) {
        state.updatePathWaypoint(state.selectedActorId, dragWaypointId.current, { pos: [nx, nz] })
      } else if (dragActorId.current) {
        state.updateAircraft(dragActorId.current, { position: [nx, nz] })
      }
    }

    const onMouseUp = (e: MouseEvent) => {
      const state = useStudioStore.getState()
      const wasDragging = !!dragActorId.current || !!dragWaypointId.current

      if (wasDragging) {
        dragActorId.current = null
        dragWaypointId.current = null
        rotating.current = false
        map.dragPan.enable()
        if (moved.current) return
      }

      // A click (no drag) — act on the current tool.
      const screen = pointerScreen(e)
      if (
        Math.hypot(screen[0] - dragStartScreen.current[0], screen[1] - dragStartScreen.current[1]) >=
        DRAG_THRESHOLD_PX
      ) {
        return
      }

      if (state.tool === 'place' && state.placingTypeCode) {
        const world = toWorldFeet(e.clientX, e.clientY)
        if (world) state.addAircraft(state.placingTypeCode, world, 0)
        return
      }

      if (state.tool === 'path' && state.selectedActorId) {
        const world = toWorldFeet(e.clientX, e.clientY)
        if (world) state.addPathWaypoint(state.selectedActorId, world)
        return
      }

      if (state.tool === 'select' && !wasDragging) {
        // Clicking empty space deselects.
        if (!pickActor(screen)) state.selectActor(null)
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      const state = useStudioStore.getState()

      if (e.key === 'Escape') {
        if (state.tool !== 'select') state.setTool('select')
        else state.selectActor(null)
        return
      }

      if (!state.selectedActorId) return

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        if (state.selectedWaypointId) {
          state.deletePathWaypoint(state.selectedActorId, state.selectedWaypointId)
        } else {
          state.deleteAircraft(state.selectedActorId)
        }
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault()
        state.duplicateAircraft(state.selectedActorId)
        return
      }

      if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        const scene = selectActiveScene(state)
        const actor = scene?.aircraft.find((a) => a.id === state.selectedActorId)
        if (!actor) return
        const delta = e.shiftKey ? -ROTATE_STEP_DEG : ROTATE_STEP_DEG
        state.updateAircraft(actor.id, {
          headingDeg: ((actor.headingDeg + delta) % 360 + 360) % 360,
        })
      }
    }

    canvas.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('keydown', onKeyDown)

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      window.removeEventListener('keydown', onKeyDown)
      map.dragPan.enable()
    }
  }, [map, center, catalog, enabled])
}
